/**
 * Dependency-free document detection for the docpic auto-capture flow.
 *
 * Given one (down-scaled) camera frame as `ImageData`, {@link analyzeDocument}
 * answers three questions the docpic screen needs:
 *
 *  1. **Where is the document?** — locate the roughly-rectangular sheet and
 *     return its bounding box, normalised to `0..1` so the caller can overlay it
 *     on the live video regardless of resolution. Two competing cues are used: a
 *     **colour** mask (Brazilian IDs are usually green, and more generally more
 *     saturated than a desk) and the classic **brightness** mask (bright paper on
 *     a darker desk). Colour wins when present, which is what lets us find a
 *     green doc that is *darker* than a light/wood background — the case a
 *     grayscale-only detector gets wrong.
 *  2. **Is the text crisp?** — a variance-of-Laplacian focus score inside the
 *     box. Sharp print has lots of high-frequency detail (high variance); a
 *     blurry/out-of-focus frame collapses towards a flat value (low variance).
 *  3. **Is it really a document?** — a content check run *before* we commit to a
 *     capture: the sheet must be paper-bright, carry a sane amount of ink-dark
 *     pixels, and — crucially — that ink must be *spread across the page* like
 *     text (many grid cells with edges), not concentrated in one blob like a
 *     hand, a mug, or a face.
 *
 * Everything runs in a handful of O(pixels) passes so it stays cheap enough to
 * call several times a second from a rAF loop. There is intentionally no
 * OpenCV/WASM dependency.
 */

/** A bounding box with all coordinates normalised to `0..1` of the frame. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Everything the docpic screen learns from a single analysed frame. */
export interface DocAnalysis {
  /** A document-like bright rectangle was located and is sensibly sized. */
  found: boolean;
  /** Bounding box of the detected sheet (normalised), or `null` when not found. */
  box: Box | null;
  /** Variance-of-Laplacian focus score inside the box (higher = sharper). */
  sharpness: number;
  /** Mean brightness of the paper inside the box (0..255). */
  brightness: number;
  /** Fraction of box pixels that are ink-dark (text strokes). */
  inkRatio: number;
  /** Fraction of grid cells inside the box that carry text-like edges. */
  textCoverage: number;
  /** Median text-line thickness as a fraction of the frame height (size proxy). */
  textLineFrac: number;
  /** Passed the "is it really a document" content check. */
  isDocument: boolean;
  /** Sharp enough to read (focus score over the threshold). */
  crisp: boolean;
  /** Text is large enough in-frame to stand a chance of being legible. */
  legible: boolean;
}

/** Tunable thresholds — surfaced so the docpic UI can show/calibrate them. */
export interface DetectThresholds {
  /** Min width/height of the sheet, as a fraction of the frame. */
  minBoxFrac: number;
  /** Max width/height of the sheet, as a fraction of the frame. */
  maxBoxFrac: number;
  /** Min share of the box that must be paper-bright (rectangular fill). */
  minFill: number;
  /** Min variance-of-Laplacian to call the text crisp. */
  sharpnessMin: number;
  /** Min mean paper brightness (rejects dark / shadowed surfaces). */
  minBrightness: number;
  /** Min ink-dark fraction (rejects a blank sheet — no text to read). */
  minInkRatio: number;
  /** Max ink-dark fraction (rejects a mostly-dark object that isn't paper). */
  maxInkRatio: number;
  /** Min fraction of grid cells with text-like edges (rejects single blobs). */
  minTextCoverage: number;
  /** Min text-line thickness (fraction of frame height) — rejects too-small/far text. */
  minTextLineFrac: number;
  /** Weight of the greenness cue (G − (R+B)/2) in the colour mask. */
  greenWeight: number;
  /** Weight of the chroma cue (max−min RGB) in the colour mask (kept lower so a
   *  wood-toned background alone doesn't qualify). */
  chromaWeight: number;
  /** Colour-foreground cutoff: a pixel is document-coloured when its weighted
   *  green+chroma score ≥ this fraction of 255. */
  colorFgFrac: number;
}

/**
 * Defaults tuned for a phone/webcam held over a document on a desk. They are
 * deliberately forgiving; the docpic screen shows the live metrics so they can
 * be calibrated against a real camera.
 */
export const DEFAULT_THRESHOLDS: DetectThresholds = {
  minBoxFrac: 0.25,
  maxBoxFrac: 0.99,
  minFill: 0.55,
  sharpnessMin: 55,
  minBrightness: 110,
  minInkRatio: 0.012,
  maxInkRatio: 0.55,
  minTextCoverage: 0.12,
  minTextLineFrac: 0.012,
  greenWeight: 1.0,
  chromaWeight: 0.4,
  colorFgFrac: 0.15,
};

/** Number of cells per side for the text-spread grid (so GRID×GRID cells). */
const GRID = 12;
/** |Laplacian| above this counts as an "edge" pixel for the text-spread check. */
const EDGE_MAG = 18;
/** A grid cell carries text if at least this fraction of its pixels are edges. */
const CELL_EDGE_FRAC = 0.02;
/** Bright-pixel coverage a row/column needs to be considered "inside" the sheet. */
const SPAN_COVERAGE = 0.4;

/**
 * Analyse one frame. The supplied `img` should be a down-scaled copy of the
 * full video frame (same aspect ratio) — ~480px wide is a good balance between
 * a meaningful focus score and per-frame cost.
 */
export function analyzeDocument(
  img: ImageData,
  th: DetectThresholds = DEFAULT_THRESHOLDS,
): DocAnalysis {
  const W = img.width;
  const H = img.height;
  const d = img.data;
  const n = W * H;

  // 1. Grayscale + intensity histogram (for Otsu) and a colour-foreground mask.
  //    The colour mask flags document-coloured pixels — green-dominant, with
  //    chroma as a weaker secondary cue — so we can locate the sheet even when
  //    it is *darker* than the background, which grayscale Otsu gets wrong.
  const gray = new Uint8Array(n);
  const hist = new Uint32Array(256);
  const colorMask = new Uint8Array(n);
  const colorCut = th.colorFgFrac * 255;
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    const r = d[p], gc = d[p + 1], b = d[p + 2];
    const g = (r * 299 + gc * 587 + b * 114) / 1000 | 0;
    gray[i] = g;
    hist[g]++;
    const green = gc - (r + b) / 2;                       // > 0 when green dominates
    const mx = r > gc ? (r > b ? r : b) : (gc > b ? gc : b);
    const mn = r < gc ? (r < b ? r : b) : (gc < b ? gc : b);
    const colorScore = (green > 0 ? green : 0) * th.greenWeight + (mx - mn) * th.chromaWeight;
    if (colorScore >= colorCut) colorMask[i] = 1;
  }
  // 2. Otsu split between a dark background and bright paper (brightness cue).
  const t = otsuThreshold(hist, n);

  // 3. Two competing candidates for the sheet: the colour mask (robust to a
  //    bright/wood background) and the classic bright-on-dark mask. Prefer the
  //    colour box when it is a sane, well-filled rectangle; otherwise fall back
  //    to brightness. With no colour at all the colour candidate is empty and
  //    behaviour collapses to the original brightness path — so nothing that
  //    works today can regress.
  let cand = maskBox(colorMask, W, H);
  if (!cand || !sizeOk(cand.box, th) || cand.fill < th.minFill) {
    const brightMask = new Uint8Array(n);
    for (let i = 0; i < n; i++) if (gray[i] > t) brightMask[i] = 1;
    const bright = maskBox(brightMask, W, H);
    if (!bright) return cand ? { ...notFound(), box: cand.box } : notFound();
    if (!sizeOk(bright.box, th) || bright.fill < th.minFill) {
      return { ...notFound(), box: bright.box };
    }
    cand = bright;
  }
  const { x0, y0, x1, y1, box } = cand;
  const bw = x1 - x0 + 1;
  const bh = y1 - y0 + 1;

  // 4. Single pass over the box: brightness/ink, Laplacian focus, edge spread.
  const cellW = bw / GRID;
  const cellH = bh / GRID;
  const cellEdge = new Uint32Array(GRID * GRID);
  const cellPix = new Uint32Array(GRID * GRID);
  /** Ink-dark pixels per row inside the box — used to size text lines. */
  const darkRow = new Uint32Array(H);

  let brightCount = 0;
  let brightSum = 0;
  let darkCount = 0;
  let lapSum = 0;
  let lapSqSum = 0;
  let lapCount = 0;
  const boxArea = bw * bh;

  for (let y = y0; y <= y1; y++) {
    const row = y * W;
    const cy = Math.min(GRID - 1, ((y - y0) / cellH) | 0);
    for (let x = x0; x <= x1; x++) {
      const g = gray[row + x];
      if (g > t) {
        brightCount++;
        brightSum += g;
      } else {
        darkCount++;
        darkRow[y]++;
      }
      const cx = Math.min(GRID - 1, ((x - x0) / cellW) | 0);
      const cell = cy * GRID + cx;
      cellPix[cell]++;

      // Laplacian needs 4-neighbours; only the box interior qualifies.
      if (x > x0 && x < x1 && y > y0 && y < y1) {
        const lap = 4 * g - gray[row + x - 1] - gray[row + x + 1] - gray[row - W + x] - gray[row + W + x];
        lapSum += lap;
        lapSqSum += lap * lap;
        lapCount++;
        if (lap < 0 ? -lap > EDGE_MAG : lap > EDGE_MAG) {
          cellEdge[cell]++;
        }
      }
    }
  }

  const brightness = brightCount ? brightSum / brightCount : 0;
  const inkRatio = darkCount / boxArea;
  const mean = lapCount ? lapSum / lapCount : 0;
  const sharpness = lapCount ? Math.max(0, lapSqSum / lapCount - mean * mean) : 0;

  let textCells = 0;
  for (let c = 0; c < cellEdge.length; c++) {
    if (cellPix[c] > 0 && cellEdge[c] / cellPix[c] >= CELL_EDGE_FRAC) {
      textCells++;
    }
  }
  const textCoverage = textCells / (GRID * GRID);

  // Text size: thickness of the horizontal ink bands (≈ text-line height). The
  // median band, as a fraction of frame height, is a resolution-independent
  // proxy for "is the document close enough for its text to be readable".
  const textLineFrac = medianBandFrac(darkRow, y0, y1, H);

  const isDocument =
    brightness >= th.minBrightness &&
    inkRatio >= th.minInkRatio &&
    inkRatio <= th.maxInkRatio &&
    textCoverage >= th.minTextCoverage;
  const crisp = sharpness >= th.sharpnessMin;
  const legible = textLineFrac >= th.minTextLineFrac;

  return {
    found: true,
    box,
    sharpness,
    brightness,
    inkRatio,
    textCoverage,
    textLineFrac,
    isDocument,
    crisp,
    legible,
  };
}

/**
 * Median thickness of the contiguous ink bands in the row projection
 * `darkRow[y0..y1]`, as a fraction of the frame height `H`. Bands are the
 * horizontal stripes of text; their typical thickness tracks the text-line
 * height. Returns `0` when there is no discernible ink.
 */
function medianBandFrac(darkRow: Uint32Array, y0: number, y1: number, H: number): number {
  let maxDark = 0;
  for (let y = y0; y <= y1; y++) {
    if (darkRow[y] > maxDark) maxDark = darkRow[y];
  }
  if (maxDark === 0) return 0;
  const lineThresh = Math.max(1, maxDark * 0.15);
  const bands: number[] = [];
  let runStart = -1;
  for (let y = y0; y <= y1; y++) {
    if (darkRow[y] >= lineThresh) {
      if (runStart < 0) runStart = y;
    } else if (runStart >= 0) {
      bands.push(y - runStart);
      runStart = -1;
    }
  }
  if (runStart >= 0) bands.push(y1 + 1 - runStart);
  if (!bands.length) return 0;
  bands.sort((a, b) => a - b);
  return bands[bands.length >> 1] / H;
}

/** A mask-derived box candidate: pixel bounds, normalised box, and how densely
 *  the mask fills that box (rectangularity / coherence proxy). */
interface MaskBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  box: Box;
  fill: number;
}

/**
 * Find the document rectangle inside a 0/1 foreground `mask` the same way the
 * original detector did with brightness: the longest contiguous row-band that
 * clears {@link SPAN_COVERAGE}, then the longest column-band within it. Returns
 * the pixel bounds, the normalised box, and the mask fill of that box, or `null`
 * if no band qualifies.
 */
function maskBox(mask: Uint8Array, W: number, H: number): MaskBox | null {
  const rowCount = new Uint32Array(H);
  for (let y = 0; y < H; y++) {
    const row = y * W;
    let c = 0;
    for (let x = 0; x < W; x++) c += mask[row + x];
    rowCount[y] = c;
  }
  const ySpan = longestRun(rowCount, W, SPAN_COVERAGE);
  if (!ySpan) return null;
  const [y0, y1] = ySpan;

  const colCount = new Uint32Array(W);
  for (let y = y0; y <= y1; y++) {
    const row = y * W;
    for (let x = 0; x < W; x++) colCount[x] += mask[row + x];
  }
  const xSpan = longestRun(colCount, y1 - y0 + 1, SPAN_COVERAGE);
  if (!xSpan) return null;
  const [x0, x1] = xSpan;

  const bw = x1 - x0 + 1;
  const bh = y1 - y0 + 1;
  let inside = 0;
  for (let y = y0; y <= y1; y++) {
    const row = y * W;
    for (let x = x0; x <= x1; x++) inside += mask[row + x];
  }
  return {
    x0, y0, x1, y1,
    box: { x: x0 / W, y: y0 / H, w: bw / W, h: bh / H },
    fill: inside / (bw * bh),
  };
}

/** Size sanity — a sizeable, non-degenerate rectangle. */
function sizeOk(box: Box, th: DetectThresholds): boolean {
  return (
    box.w >= th.minBoxFrac &&
    box.h >= th.minBoxFrac &&
    box.w <= th.maxBoxFrac &&
    box.h <= th.maxBoxFrac
  );
}

/** Whether two boxes are close enough to be "the same" (stability gate). */
export function boxesClose(a: Box, b: Box, tol = 0.06): boolean {
  return (
    Math.abs(a.x - b.x) < tol &&
    Math.abs(a.y - b.y) < tol &&
    Math.abs(a.w - b.w) < tol &&
    Math.abs(a.h - b.h) < tol
  );
}

function notFound(): DocAnalysis {
  return {
    found: false,
    box: null,
    sharpness: 0,
    brightness: 0,
    inkRatio: 0,
    textCoverage: 0,
    textLineFrac: 0,
    isDocument: false,
    crisp: false,
    legible: false,
  };
}

/**
 * Otsu's method: pick the intensity that maximises between-class variance,
 * i.e. the cleanest split of the histogram into dark vs. bright pixels.
 */
function otsuThreshold(hist: Uint32Array, total: number): number {
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let best = 0;
  let bestVar = -1;
  for (let i = 0; i < 256; i++) {
    wB += hist[i];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += i * hist[i];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > bestVar) {
      bestVar = between;
      best = i;
    }
  }
  return best;
}

/**
 * Longest contiguous run of indices whose `counts[i] / denom` clears `frac`.
 * Used to find the document's vertical (then horizontal) extent from a bright-
 * pixel projection. Returns inclusive `[start, end]`, or `null` if none.
 */
function longestRun(counts: Uint32Array, denom: number, frac: number): [number, number] | null {
  const need = denom * frac;
  let bestStart = -1;
  let bestEnd = -1;
  let curStart = -1;
  for (let i = 0; i < counts.length; i++) {
    if (counts[i] >= need) {
      if (curStart < 0) curStart = i;
      if (i - curStart > bestEnd - bestStart) {
        bestStart = curStart;
        bestEnd = i;
      }
    } else {
      curStart = -1;
    }
  }
  return bestStart < 0 ? null : [bestStart, bestEnd];
}
